import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

import { CookieService } from 'ngx-cookie-service';
import { StorageService } from './storage.service';
import { AppUser } from '../models/app.user';

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';

import { DeviceDetectorService } from 'ngx-device-detector';

declare var dataLayer: any;

@Injectable()
export class LoginService {

  public version: string = "2.60.0";

  private URL: string = environment.urlbase + 'login';
  private logged: boolean = false;
  public storedLoggedUser: AppUser = null;

  private storedToken: string;

  private deviceInfo: {browser: string, browser_version: string, device: string, os: string, os_version: string, userAgent: string};
  private loginError: {status?: number, message?: string} = {status: 0, message: ''};

  public user$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  public loggedUser$: BehaviorSubject<AppUser> = new BehaviorSubject(null);

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private storageService: StorageService,
    private deviceService: DeviceDetectorService
  ) { }

  /* =================================================
     ---------- A U T H   F U N C T I O N S ----------
     =================================================*/

  login(email: string, password: string): Promise<boolean> {
    let loginData: any = { email: email, password: password, type: 'PWA' };
    let headersData: any = this.deviceDetector();
    return new Promise<boolean>(
      (logged: any): void => {
        this.http.post(this.URL, loginData, {headers: headersData})
          .subscribe((auth: AppUser): void => {
            this.purge();
            this.storeToken(auth.api_token);
            this.fetchLoggedUser()
            .subscribe(
              (user: AppUser): void => {
                this.dispatchEvent('login-pwa', user);
                this.storedLoggedUser = user;
                this.logged = true;
                logged(true);
              },
              (): void => {
                this.storedLoggedUser = null;
                this.logged = false;
                logged(false);
              }
            );
          },
          (error: HttpErrorResponse): void => {
            this.purge();
            this.logged = false;
            this.storedLoggedUser = null;
            this.loginError = {status: error.status, message: error.error.message};
            logged(false);
          },
        );
      },
    );
  }

  logout(): void {
    this.logged = false;
    this.storedToken = '';
    this.storedLoggedUser = null;
    this.storageService.store('', this.normalizeString(environment.appname) + '_app_user_token');
    this.storageService.clearKeysWithDoubleUnderscore();
    this.purge();
  }

  resetPassword(data: any): Observable<any> {
    return this.http.post((environment.urlbase + 'resetpassword'), data);
  }

  deleteAccount(): Observable<any> {
    return this.http.delete<any>(environment.urlbase + "user", { headers: this.getHeaders() });
  }

  getLoginError(): {status?: number, message?: string} {
    return this.loginError;
  }

  forceLogin(data: AppUser): void {
    this.storedLoggedUser = data;
    this.purge();
    this.storeToken(data.api_token);
  }

  purge(): void {
    this.cookieService.delete(this.normalizeString(environment.appname) + '_app_user_token');
  }

  getToken(): string {
    if (this.storedToken == '') {
      return "";
    }
    if (this.storedToken !== null && this.storedToken !== undefined && this.storedToken != '') {
      this.checkConsentAndStoreTokenCookie(this.storedToken);
      return this.storedToken;
    }
    if (this.storedLoggedUser !== null && this.storedLoggedUser !== undefined) {
      const loggedUserToken = this.storedLoggedUser.api_token;
      if (loggedUserToken != '' && loggedUserToken !== null && loggedUserToken !== undefined && loggedUserToken != 'undefined') {
        this.storedToken = loggedUserToken;
        this.checkConsentAndStoreTokenCookie(this.storedToken);
        return loggedUserToken;
      }
    }
    const storageToken: string = this.storageService.restore(this.normalizeString(environment.appname) + '_app_user_token');
    if (storageToken != '' && storageToken !== null && storageToken !== undefined && storageToken != 'undefined') {
      this.checkConsentAndStoreTokenCookie(storageToken);
      this.storedToken = storageToken;
      return storageToken;
    }
    const cookieToken = this.cookieService.get(this.normalizeString(environment.appname) + '_app_user_token');
    if (cookieToken != '' && cookieToken !== null && cookieToken !== undefined && cookieToken != 'undefined') {
      this.storedToken = cookieToken;
      return cookieToken;
    }
    return "";
  }

  storeToken(api_token: string): void {
    if (api_token !== null && api_token !== undefined && api_token != '') {
      this.storedToken = api_token;
      this.storageService.store(api_token, this.normalizeString(environment.appname) + '_app_user_token');
      this.checkConsentAndStoreTokenCookie(api_token);
    }
  }

  /* =================================================
     ---------- U S E R   F U N C T I O N S ----------
     =================================================*/

  isAuthenticated(): boolean {
    return this.logged || this.getToken() !== "";
  }

  public fetchLoggedUser(): Observable<AppUser> {
    return this.http.get<AppUser>(environment.urlbase + 'user', { headers: this.getHeaders() });
  }

  refreshLoggedUser(): void {
    this.fetchLoggedUser()
    .subscribe(
      (authUser: AppUser): void => {
        this.storedLoggedUser = authUser;
        this.logged = false;
        this.loggedUser$.next(authUser);
      },
      (error: Error): void => {
        this.storedLoggedUser = null;
        this.logged = false;
        console.error("não foi possível buscar o usuário: ", error);
      }
    );
  }

  loggedUser(): Promise<AppUser> {
    return new Promise<AppUser>(
      (next): void => {
        if (this.storedLoggedUser !== null && this.storedLoggedUser !== undefined) {
          next(this.storedLoggedUser);
        } else {
          this.fetchLoggedUser()
          .subscribe(
            (authUser: AppUser): void => {
              if (authUser) {
                this.storedLoggedUser = authUser;
                this.loggedUser$.next(authUser);
              }
              next(authUser);
            },
            (error: Error): void => {
              next(null);
              console.error("não foi possível buscar o usuário: ", error);
              this.logout()
            }
          );
        }
      }
    );
  }

  updateLoggedUser(property: string, value: any): void {
    this.storedLoggedUser[property] = value;
  }

  /* =======================================================
     ---------- C O O K I E S   F U N C T I O N S ----------
     =======================================================*/

  checkConsentAndStoreTokenCookie(api_token: string) {
    if (this.isCookieConsent()) {
      this.cookieService.set(this.normalizeString(environment.appname) + '_app_user_token', api_token, { sameSite: 'Strict' });
    }
  }

  isCookieConsent(): boolean {
    const ckCookie: boolean = this.cookieService.get(this.normalizeString(environment.appname) + '_cookie_consent') == 'true';
    const ckStorage: boolean = this.storageService.restore(this.normalizeString(environment.appname) + '_cookie_consent') == 'true';
    return ckCookie || ckStorage;
  }

  consentCookie(): void {
    this.cookieService.set(this.normalizeString(environment.appname) + '_cookie_consent', 'true', { sameSite: 'Strict' });
    this.storageService.store('true', this.normalizeString(environment.appname) + '_cookie_consent');
  }

  getCookie(name: string): string {
    return this.cookieService.get(this.normalizeString(environment.appname) + '_' + name);
  }

  putCookie(name: string, value: string): void {
    this.cookieService.set(this.normalizeString(environment.appname) + '_' + name, value, { sameSite: 'Strict' });
  }

  purgeCookie(name: string): void {
    this.cookieService.delete(this.normalizeString(environment.appname) + '_' + name);
  }

  /* ===============================================
     ---------- G T M   F U N C T I O N S ----------
     ===============================================*/

  hasGTM(): boolean {
    const gtmScript: HTMLLinkElement = document.getElementById('gtm-script') as HTMLLinkElement;
    return gtmScript !== null && gtmScript !== undefined;
  }

  dispatchEvent(event: string, user: AppUser): void {
    if (this.hasGTM() && this.exists(dataLayer) && this.exists(dataLayer.push)) {
      dataLayer.push({'event': event, 'email': user.email, 'name': user.name});
    }
  }

  /* =====================================================
     ---------- D E V I C E   F U N C T I O N S ----------
     =====================================================*/

  deviceDetector(): {"os-version": string, "device-model": string, "os": string, "device-id": string, "app-version": string} {
    this.deviceInfo = this.deviceService.getDeviceInfo();
    let deviceId: string = this.getCookie('device_id');
    if (deviceId === null || deviceId === undefined || deviceId == '') {
      deviceId = Math.floor((Math.random() * 1000000000000) + 100000000000).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "-");
      this.putCookie('device_id', deviceId);
    }
    return {
      "os-version": this.deviceInfo.os_version,
      "device-model": this.deviceInfo.device == 'unknown' ? this.deviceInfo.browser : this.deviceInfo.device,
      "os": 'Web',
      "app-version": this.version,
      "device-id": deviceId
    }
  }

  getDecToken(): string {
    const userHashToken: string = environment.ds + this.getToken().replace('Bearer ', '');
    var hexaDecimalString: string[] = [];
    for (let i: number = 0; i < userHashToken.length; i++) {
      hexaDecimalString[i] = userHashToken.charCodeAt(i).toString(16).replace(/^([\da-f])$/,"0$1");
    }
    return (hexaDecimalString.join('')).substring(0, 32);
  }

  /* =======================================================
     ---------- G E N E R A L   F U N C T I O N S ----------
     =======================================================*/

  getHeaders(externalToken = null): HttpHeaders {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.set('Accept', 'application/json');
    const token = externalToken == null ? this.getToken() : externalToken;
    if (token !== undefined && token != '' && token !== null) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }
    for (const [key, value] of Object.entries(this.deviceDetector())) {
      if (this.exists(value)) headers = headers.set(key, value);
    }
    return headers;
  }

  private normalizeString(text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\ /g, '_');
  }

  exists(model: any): boolean {
    return model !== null && model !== undefined && model != '';
  }
}
